import { z } from 'zod';

import { BundleDepthSchema, BundleModeSchema, type BundleDepth, type BundleMode } from './bundle-ops.js';
import { type EntityRoot } from './pack.js';

/**
 * Bulk world-generation request + the deterministic worker helpers it needs
 * (#251 part b, 04-llm-role.md §14). The *request type* is shared because the
 * part-c review UI constructs it and POSTs it to the worker; the *helpers*
 * (mode resolution, fan-out cap) are pure so they are unit-testable without a
 * worker/DO and reusable by any future producer.
 *
 * The worker route grounds the prompt, runs the LLM through the AI Gateway, and
 * atomically appends the resulting bundle (`bundle.declare` + N llm-draft
 * `fact.create` + entity stubs) via a new DO RPC. NONE of this writes canon —
 * every appended fact is `status: 'draft'`; bundle-accept (part c) is the only
 * path to canon and is GM-authored.
 */

// ─── Request ───────────────────────────────────────────────────────────────

/**
 * The two entry surfaces (an entity-context action + a campaign-bootstrap
 * entry) construct the SAME request shape so the worker has one code path
 * (decision 6). An `entity` target fans out FROM an established entity; a
 * `bootstrap` target seeds a region/world from (almost) nothing.
 */
export const BulkGenTargetSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('entity'), entity_id: z.string().min(1) }).strict(),
  z.object({ kind: z.literal('bootstrap'), seed: z.string().min(1).optional() }).strict(),
]);
export type BulkGenTarget = z.infer<typeof BulkGenTargetSchema>;

/** Default generation depth (decision 6 — `sparse` is the conservative default). */
export const DEFAULT_BULK_GEN_DEPTH: BundleDepth = 'sparse';
/** Default grounding mode — `auto` lets the worker heuristic resolve it. */
export const DEFAULT_BULK_GEN_MODE: BundleMode = 'auto';

/**
 * A bulk-generation request. `depth` shapes fan-out WITHIN the hard cap (#218);
 * `mode` is the grounding posture (`auto` → worker heuristic, else GM override).
 * Defaults are applied by the schema so a minimal `{ target, prompt }` parses.
 */
export const BulkGenRequestSchema = z
  .object({
    target: BulkGenTargetSchema,
    /** Free-text generation direction (model input, not a UI string). */
    prompt: z.string().min(1),
    depth: BundleDepthSchema.default(DEFAULT_BULK_GEN_DEPTH),
    mode: BundleModeSchema.default(DEFAULT_BULK_GEN_MODE),
  })
  .strict();
export type BulkGenRequest = z.infer<typeof BulkGenRequestSchema>;

/** Parse + default a bulk-gen request, throwing on a malformed body. */
export function parseBulkGenRequest(body: unknown): BulkGenRequest {
  return BulkGenRequestSchema.parse(body);
}

// ─── Mode auto-resolution (deterministic heuristic) ──────────────────────────

/** The campaign-density signals the auto heuristic reads (decision 2 / §14.6). */
export interface CampaignDensity {
  /** Total live (non-soft-deleted) entities in the campaign canon. */
  entityCount: number;
  /**
   * For an entity target: how many established neighbors it has (graph edges to
   * other live entities). Ignored for a bootstrap target. 0 for an island.
   */
  neighborCount: number;
}

/**
 * Below this entity count a campaign has "little canon" — even an entity target
 * is treated as a bootstrap (nothing to ground against). Deliberately small:
 * once a world has a few dozen entities, an entity with neighbors grounds well.
 */
export const BULK_GEN_SPARSE_CANON_THRESHOLD = 12;
/** An entity target needs at least this many neighbors to ground `in_world`. */
export const BULK_GEN_MIN_NEIGHBORS = 1;

/**
 * Deterministically resolve the grounding mode (decision 2 / §14.6).
 *
 *   - A GM override (`requested` ≠ `'auto'`) wins verbatim — no heuristic.
 *   - A `bootstrap` target is always `bootstrap` (it seeds from a seed string,
 *     not from neighbor canon).
 *   - An `entity` target resolves to `in_world` ONLY when the campaign has
 *     enough canon AND the target has established neighbors; otherwise
 *     `bootstrap` (an island entity, or a near-empty campaign, has nothing to
 *     ground against, so the bootstrap/creative posture is correct).
 *
 * Pure + total — same inputs always yield the same resolved mode (the worker
 * records it on `bundle.declare`). Never returns `'auto'` (it is fully resolved).
 */
export function resolveBulkMode(
  requested: BundleMode,
  target: BulkGenTarget,
  density: CampaignDensity,
): Exclude<BundleMode, 'auto'> {
  if (requested !== 'auto') return requested;
  if (target.kind === 'bootstrap') return 'bootstrap';
  const grounded =
    density.entityCount >= BULK_GEN_SPARSE_CANON_THRESHOLD &&
    density.neighborCount >= BULK_GEN_MIN_NEIGHBORS;
  return grounded ? 'in_world' : 'bootstrap';
}

// ─── Fan-out hard cap + truncation ───────────────────────────────────────────

/**
 * The config-backed hard cap (decision 7 / §14.7). `depth` shapes fan-out
 * WITHIN this bound; the worker never appends more than this regardless of what
 * the LLM proposes. A sensible default (~25 entities / 150 facts per bundle);
 * an admin/campaign override surface can later widen it, but this constant is
 * the documented bound when no override is set.
 */
export const BULK_GEN_FANOUT_CAP = {
  maxEntities: 25,
  maxFacts: 150,
} as const;

/**
 * A single proposed bundle member, before truncation/append. The LLM produces a
 * value + predicate + subject and a self-rated `confidence`; `newEntity` is
 * present when the subject is a NEW stub the bundle must create (an
 * `entity.create` is appended alongside the member's `fact.create`).
 */
export interface ProposedBundleMember {
  subject: string;
  predicate: string;
  value: unknown;
  /** LLM self-rated confidence 0–1 (#251 §14.6). */
  confidence: number;
  /** Present iff `subject` is a new stub this bundle introduces. */
  newEntity?: { root: EntityRoot };
}

/** What was truncated when the cap bit (surfaced on the bundle — no silent drop). */
export interface BundleCapInfo {
  requested_facts: number;
  requested_entities: number;
  max_facts: number;
  max_entities: number;
}

export interface CappedBundle {
  members: ProposedBundleMember[];
  /** Null when nothing was truncated; the cap info otherwise. */
  capped: BundleCapInfo | null;
}

/**
 * Apply the fan-out hard cap (decision 7). `depth` is accepted so the cap can
 * later be depth-scaled WITHIN the bound; today it shapes the generation prompt
 * (the worker), and the cap is the absolute ceiling. Truncation rule:
 *
 *   1. Cap distinct NEW entities to `maxEntities` — members introducing an
 *      entity beyond the cap are dropped, AND every later member targeting a
 *      dropped new entity is dropped too (a dangling fact on a never-created
 *      stub would be a broken member). Facts on already-EXISTING entities are
 *      never entity-capped.
 *   2. Cap total facts to `maxFacts`.
 *
 * When anything is dropped, `capped` records the originally-requested counts +
 * the bounds so the bundle can surface "capped at N" (no silent drop).
 */
export function capBundleFanOut(
  members: readonly ProposedBundleMember[],
  _depth: BundleDepth,
): CappedBundle {
  const requestedFacts = members.length;
  const requestedEntities = new Set(
    members.filter((m) => m.newEntity).map((m) => m.subject),
  ).size;

  // 1. New-entity cap: admit new entities in first-seen order until the cap;
  //    a member on a dropped new entity is itself dropped.
  const admittedNewEntities = new Set<string>();
  const droppedNewEntities = new Set<string>();
  const afterEntityCap: ProposedBundleMember[] = [];
  for (const m of members) {
    if (m.newEntity) {
      if (!admittedNewEntities.has(m.subject) && !droppedNewEntities.has(m.subject)) {
        if (admittedNewEntities.size < BULK_GEN_FANOUT_CAP.maxEntities) {
          admittedNewEntities.add(m.subject);
        } else {
          droppedNewEntities.add(m.subject);
        }
      }
      if (droppedNewEntities.has(m.subject)) continue;
    } else if (droppedNewEntities.has(m.subject)) {
      // A later fact on a new entity we already dropped — drop it too.
      continue;
    }
    afterEntityCap.push(m);
  }

  // 2. Fact cap: keep the first `maxFacts` of the entity-capped list.
  const finalMembers = afterEntityCap.slice(0, BULK_GEN_FANOUT_CAP.maxFacts);

  const truncated =
    finalMembers.length !== requestedFacts ||
    requestedEntities > BULK_GEN_FANOUT_CAP.maxEntities;

  return {
    members: finalMembers,
    capped: truncated
      ? {
          requested_facts: requestedFacts,
          requested_entities: requestedEntities,
          max_facts: BULK_GEN_FANOUT_CAP.maxFacts,
          max_entities: BULK_GEN_FANOUT_CAP.maxEntities,
        }
      : null,
  };
}
